// Client-side batching for PATCH /api/skus/bulk-active. The server caps the
// `ids` array at 500 (Zod .max(500) in apps/worker/src/routes/skus.ts →
// bulkSkuActiveSchema); sending more returns 400. Selecting all disabled SKUs
// (e.g. 606) and hitting "Enable selected" therefore failed — and the client
// swallowed the 400. Fix: keep the server cap, split the client request into
// sequential batches of ≤500, aggregate the counts, and surface failures
// (which batch, how many had already succeeded) so nothing fails silently.
//
// Pure + injectable (the sender is passed in) so the batching, aggregation, and
// partial-failure reporting are unit-testable without fetch.

/** Mirror of the server's per-request id cap (skus.ts bulkSkuActiveSchema.max). */
export const BULK_ACTIVE_MAX = 500;

/** Split a list into contiguous chunks of at most `size`. */
export function splitBatches<T>(items: readonly T[], size: number): T[][] {
  if (size <= 0) throw new Error('batch size must be > 0');
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Thrown when a batch fails — carries which batch + how many already succeeded. */
export class BulkActiveBatchError extends Error {
  readonly batchIndex: number; // 0-based index of the failed batch
  readonly batchesTotal: number;
  readonly succeeded: number; // ids confirmed updated before the failure
  override readonly cause: unknown;
  constructor(batchIndex: number, batchesTotal: number, succeeded: number, cause: unknown) {
    const reason = cause instanceof Error ? cause.message : String(cause);
    super(
      `Batch ${batchIndex + 1} of ${batchesTotal} failed (${succeeded} already updated): ${reason}`,
    );
    this.name = 'BulkActiveBatchError';
    this.batchIndex = batchIndex;
    this.batchesTotal = batchesTotal;
    this.succeeded = succeeded;
    this.cause = cause;
  }
}

/**
 * Run a bulk active/inactive flip in sequential batches of ≤`size`, aggregating
 * the `updated` count. On a batch failure, throws a BulkActiveBatchError that
 * reports the failed batch + the count that already succeeded (no silent loss).
 * `sendBatch` performs ONE server request for a single ≤size batch.
 */
export async function runBatchedBulkActive(
  ids: readonly string[],
  active: boolean,
  sendBatch: (batchIds: string[], active: boolean) => Promise<{ updated: number }>,
  size: number = BULK_ACTIVE_MAX,
): Promise<{ updated: number }> {
  const batches = splitBatches(ids, size);
  let updated = 0;
  for (let i = 0; i < batches.length; i++) {
    try {
      const res = await sendBatch(batches[i]!, active);
      updated += res.updated;
    } catch (cause) {
      throw new BulkActiveBatchError(i, batches.length, updated, cause);
    }
  }
  return { updated };
}

/** Success-toast text for a completed bulk flip, e.g. "Enabled 606 SKUs". */
export function describeBulkActiveResult(updated: number, active: boolean): string {
  const verb = active ? 'Enabled' : 'Disabled';
  return `${verb} ${updated} SKU${updated === 1 ? '' : 's'}`;
}
